import { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../services/api';
import cookieManager, { isUserLoggedIn } from '../utils/cookieUtils';
import AUTH_CONFIG from '../config/authConfig';

// Cookie names for last known auth state (abbreviated for security)
const LAST_USER_COOKIE = AUTH_CONFIG.COOKIE_NAMES.LAST_USER;
const LAST_AUTH_COOKIE = AUTH_CONFIG.COOKIE_NAMES.LAST_AUTH;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to get last known user/isAuthenticated from cookies
export function getLastKnownAuth() {
  let user = null;
  let isAuthenticated = false;
  
  try {
    const userStr = cookieManager.getCookie(LAST_USER_COOKIE);
    const authStr = cookieManager.getCookie(LAST_AUTH_COOKIE);
    
    if (userStr) user = JSON.parse(decodeURIComponent(atob(userStr)));
    if (authStr) isAuthenticated = JSON.parse(decodeURIComponent(atob(authStr)));
  } catch (e) {
    console.warn('Error parsing last known auth data:', e);
  }
  
  return { user, isAuthenticated };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Persist last known user/isAuthenticated for optimistic UI using cookies
  useEffect(() => {
    if (!loading) {
      try {
        // Use encodeURIComponent + btoa to handle Unicode characters safely
        const userData = btoa(encodeURIComponent(JSON.stringify(user)));
        const authData = btoa(encodeURIComponent(JSON.stringify(!!user))); // HttpOnly: check user existence instead of token
        
        cookieManager.setPreferenceCookie(LAST_USER_COOKIE, userData, 30);
        cookieManager.setPreferenceCookie(LAST_AUTH_COOKIE, authData, 30);
      } catch (e) {
        console.warn('Error saving auth state to cookies:', e);
      }
    }
  }, [user, loading]);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      try {
        // First check login status cookie to avoid unnecessary API calls for guests
        if (!isUserLoggedIn()) {
          setUser(null);
          return;
        }

        // Login status cookie indicates user might be logged in, verify with profile fetch
        const profile = await ApiService.getUserProfile();
        setUser(profile);
      } catch (err) {
        // If profile fetch fails, user is not authenticated
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      // Guest ID is now managed by backend HTTP-only cookies
      await ApiService.login(email, password);
      // With HttpOnly cookies, tokens are set automatically by backend
      // Always fetch the latest profile after login
      const profile = await ApiService.getUserProfile();
      setUser(profile);
      // Cart will be fetched automatically by CartContext when user is authenticated
      return profile;
    } catch (error) {
      // Enhanced error handling for better user experience
      let errorMessage = error.message;
      
      // Handle specific authentication error types
      if (errorMessage.includes('Network error during authentication')) {
        errorMessage = AUTH_CONFIG.ERROR_MESSAGES.NETWORK_ERROR;
      } else if (errorMessage.includes('Session expired')) {
        errorMessage = AUTH_CONFIG.ERROR_MESSAGES.SESSION_EXPIRED;
      } else if (errorMessage.includes('Authentication failed after maximum retry attempts')) {
        errorMessage = AUTH_CONFIG.ERROR_MESSAGES.MAX_ATTEMPTS_EXCEEDED;
      }
      
      setError(errorMessage);
      throw error;
    }
  };

  const register = async (firstName, lastName, email, password) => {
    try {
      setError(null);
      const result = await ApiService.register(firstName, lastName, email, password);
      return { emailVerificationSent: result.emailVerificationSent };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await ApiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setError(null);
      // Cart cookies are now cleared by the backend during logout
    }
  };

  const forgotPassword = async (email) => {
    try {
      setError(null);
      return await ApiService.forgotPassword(email);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const resetPassword = async (token, password) => {
    try {
      setError(null);
      return await ApiService.resetPassword(token, password);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      const response = await ApiService.updateProfile(profileData);
      // Update state with new user data
      const updatedUser = { ...user, ...response };
      setUser(updatedUser);
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    isAuthenticated: !!user, // With HttpOnly cookies, check user existence instead of token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 